import { songQuery } from '../../domain/song-search';
import { findStation } from '../../domain/stations';
import { translate } from '../../i18n';
import { el, icon } from '../../lib/dom';
import { trapFocus } from '../../lib/ds';
import { keepScreenAwake, type WakeLockHandle } from '../../lib/wake-lock';
import { type AppState, type PlayerMode, playableStations } from '../../store/app';
import { formatBitrate, formatRoute, formatTrack, joinMeta } from '../format';
import { createSongMenu } from './song-menu';
import { createVolume, type VolumeDeps } from './volume';

/**
 * Razširjeni predvajalnik (P2 §3.11) in **način za avto** (P2 §7) — DS
 * `.nowplaying-full`, od v4.22.0 ([D2 §38](../../../docs/04-delivery/D2-ds-requests.md)).
 *
 * ⚠️ **Ena komponenta, dva pogleda.** Način za avto ni druga površina, ampak
 * ista z razredom `.car`: cilji `--target-lg` (76px), tip `--text-3xl` in
 * **samo trije kontrolniki**. Mere pridejo iz tokenov DS — v tej datoteki ni
 * niti ene pikselske vrednosti.
 *
 * ⚠️ **Vrstica se med tem SKRIJE, in to je pogoj, ne kozmetika.** Prehod
 * vrstica ↔ celozaslon teče prek View Transitions, ta pa zahteva, da je
 * `view-transition-name` v dokumentu **enkraten**. Naslovnica vrstice in
 * naslovnica celega zaslona nosita isto ime (`nowplaying-art`); če bi bili
 * hkrati vidni, bi se prehod tiho odpovedal. Element z `display: none` v
 * zajem ne gre — zato `#player` dobi `hidden`, kadar je ta pogled odprt.
 *
 * ⚠️ **Wake lock samo v načinu za avto** (P2 §7.3). Razširjen pogled je
 * običajno gledanje in nima razloga držati zaslona prižganega; med vožnjo pa
 * je ugasel zaslon natanko tisto, čemur se način izogiba.
 */

export interface PlayerFullDeps extends VolumeDeps {
  onToggle(): void;
  onPrevious(): void;
  onNext(): void;
  /** Zapri pogled (gumb, Esc). Gumb nazaj gre po svoji poti — glej `main.ts`. */
  onClose(): void;
  onCarMode(on: boolean): void;
}

export interface PlayerFullNode {
  root: HTMLElement;
  update(state: AppState): void;
}

export function createPlayerFull(host: HTMLElement, deps: PlayerFullDeps): PlayerFullNode {
  const car = el('button', { class: 'nowplaying-full__close', type: 'button' });
  const close = el('button', { class: 'nowplaying-full__close', type: 'button' });
  close.addEventListener('click', deps.onClose);

  const art = el('div', { class: 'nowplaying-full__art' }, [icon('i-music', 64)]);
  const cover = el('img', { alt: '', decoding: 'async' });
  let coverSrc: string | null = null;

  const title = el('div', { class: 'nowplaying-full__title' });
  const station = el('div', { class: 'nowplaying-full__station' });
  /**
   * Tehnična in zasebnostna meta: **kodek · bitrate · pot toka**.
   *
   * Prestavljena iz medijske vrstice (2026-07-23): tam so bili trdi kosi za
   * imenom postaje in na telefonu so dolgo ime potiskali z zaslona. Tu je
   * prostor in je en dotik stran. Nevtralno, mono — kakovost ni status
   * (CLAUDE.md, signal ≠ status), pot toka pa je signal zasebnosti in ni
   * izginila, le preselila se je.
   */
  const meta = el('div', { class: 'nw-full__meta tnum' });

  const previous = el('button', { class: 'nowplaying-full__btn', type: 'button' }, [
    icon('i-skip-back', 26),
  ]);
  const toggle = el('button', { class: 'nowplaying-full__btn play', type: 'button' });
  const next = el('button', { class: 'nowplaying-full__btn', type: 'button' }, [
    icon('i-skip-forward', 26),
  ]);
  previous.addEventListener('click', deps.onPrevious);
  toggle.addEventListener('click', deps.onToggle);
  next.addEventListener('click', deps.onNext);

  const volume = createVolume(deps, 'fullVolume');
  const songMenu = createSongMenu('nowplaying-full__btn');

  /**
   * Glasnost in iskanje skladbe sta **samo v razširjenem pogledu**.
   *
   * P2 §7.2 je pri načinu za avto izrecen: *samo trije kontrolniki*. Vsak
   * dodaten je med vožnjo cilj, ki ga uporabnik ne sme iskati.
   */
  const extras = el('div', { class: 'nw-full__extras' }, [volume.root, songMenu.root]);

  /**
   * ⚠️ **Zakaj drsnika ni — pove se na glas** (P2 §6.5).
   *
   * Na WebKitu `HTMLMediaElement.volume` ne dela in ostane sam gumb za tišino.
   * Do B-5 (2026-07-22) je to razlagala vrstica glasnosti v Nastavitvah; ta je
   * odpadla kot podvojitev, razlaga pa je šla z njo sem — sicer bi iPhone dobil
   * en sam gumb brez pojasnila in uporabnik bi napako iskal pri sebi.
   *
   * V načinu za avto ga ni, iz istega razloga kot `extras`.
   */
  const volumeNote = el('p', { class: 'nw-note nw-full__note', hidden: '' });

  const root = el('div', {
    class: 'nowplaying-full nw-full',
    role: 'dialog',
    'aria-modal': 'true',
    hidden: '',
  });
  root.append(
    el('div', { class: 'nowplaying-full__top' }, [car, close]),
    el('div', { class: 'nowplaying-full__body' }, [art, title, station, meta]),
    el('div', { class: 'nowplaying-full__foot' }, [
      el('div', { class: 'nowplaying-full__ctrls' }, [previous, toggle, next]),
      extras,
      volumeNote,
    ]),
  );
  host.append(root);

  let mode: PlayerMode = 'bar';
  let release: (() => void) | null = null;
  let wakeLock: WakeLockHandle | null = null;
  let opener: HTMLElement | null = null;

  car.addEventListener('click', () => {
    deps.onCarMode(mode !== 'car');
  });

  // Esc zapre — ista pot kot gumb. Poslušalec je na dokumentu, ker fokus med
  // tem lahko leži na katerem koli kontrolniku znotraj pogleda.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || mode === 'bar') return;
    event.preventDefault();
    deps.onClose();
  });

  function renderCover(url: string | null): void {
    if (url === coverSrc) return;
    coverSrc = url;
    if (url === null) {
      cover.remove();
      return;
    }
    cover.src = url;
    if (cover.parentNode === null) art.append(cover);
  }

  /** Odpiranje, zapiranje in preklop v avto — vse, kar ni vsebina. */
  function applyMode(next: PlayerMode): void {
    if (next === mode) return;
    const wasOpen = mode !== 'bar';
    mode = next;

    root.classList.toggle('car', next === 'car');
    root.toggleAttribute('hidden', next === 'bar');
    extras.toggleAttribute('hidden', next !== 'full');
    // Razlaga stoji samo tam, kjer je tudi vzrok: v razširjenem pogledu in le,
    // kadar platforma glasnosti res ne da nastaviti.
    volumeNote.toggleAttribute('hidden', next !== 'full' || deps.volumeSupported());

    // Zaslon budan samo med vožnjo (P2 §7.3).
    if (next === 'car' && wakeLock === null) wakeLock = keepScreenAwake();
    if (next !== 'car' && wakeLock !== null) {
      wakeLock.release();
      wakeLock = null;
    }

    if (next === 'bar') {
      release?.();
      release = null;

      /**
       * Fokus se vrne tja, od koder je uporabnik prišel — sicer skoči na
       * začetek dokumenta in mesto v seznamu je izgubljeno.
       *
       * ⚠️ **Mikroopravilo, ne neposreden klic.** Sprožilec je pogosto gumb v
       * medijski vrstici, ta pa je med odprtim pogledom `[hidden]` in se
       * odkrije šele v `syncChrome()` — torej **pozneje v istem izrisu**.
       * Neposreden `focus()` bi zadel še skrit element in tiho ne naredil
       * ničesar. Ujel je E2E, ne pregled kode.
       */
      const target = opener;
      opener = null;
      if (target !== null) queueMicrotask(() => target.focus());
      return;
    }

    if (!wasOpen) {
      const active = document.activeElement;
      opener = active instanceof HTMLElement && active !== document.body ? active : null;
      release = trapFocus(root);
    }
  }

  return {
    root,
    update(state) {
      applyMode(state.player);
      if (state.player === 'bar') return;

      const { lang } = state;
      const current = findStation(playableStations(state), state.stationId);
      const track = state.track;

      root.setAttribute('aria-label', translate(lang, 'player.expanded'));

      title.textContent =
        track !== null
          ? formatTrack(lang, track)
          : (current?.name ?? translate(lang, 'player.nothing'));
      // Ime postaje se ne podvoji: kadar naslova skladbe ni, stoji zgoraj.
      station.textContent = track !== null ? (current?.name ?? '') : '';
      // `joinMeta` izpusti manjkajoče kose: pot je `null`, dokler tok ne teče,
      // bitrate pa ga marsikatera postaja ne pove.
      meta.textContent = joinMeta([
        current?.codec ?? null,
        formatBitrate(lang, current?.bitrate),
        formatRoute(lang, state.route),
      ]);
      renderCover(state.artworkUrl);

      const active =
        state.playback === 'playing' ||
        state.playback === 'loading' ||
        state.playback === 'reconnecting';
      const label = translate(lang, active ? 'player.stop' : 'player.play');
      toggle.setAttribute('aria-label', label);
      toggle.title = label;
      toggle.replaceChildren(icon(active ? 'i-stop' : 'i-play', 26));

      previous.setAttribute('aria-label', translate(lang, 'player.previous'));
      next.setAttribute('aria-label', translate(lang, 'player.next'));

      const carLabel = translate(lang, state.player === 'car' ? 'player.car.exit' : 'player.car');
      car.setAttribute('aria-label', carLabel);
      car.title = carLabel;
      car.replaceChildren(icon(state.player === 'car' ? 'i-x-circle' : 'i-car', 22));

      const closeLabel = translate(lang, 'player.collapse');
      close.setAttribute('aria-label', closeLabel);
      close.title = closeLabel;
      close.replaceChildren(icon('i-fullscreen-exit', 22));

      volume.update(state);
      volumeNote.textContent = translate(lang, 'player.volume.unsupported');
      songMenu.update(lang, track === null ? null : songQuery(track));
    },
  };
}
